/**
 * Hiển thị tiền có ký hiệu (`500.000 ₫` · `₫500,000` · `12.500,00 US$`) từ BigInt minor units — MỘT nguồn cho
 * thông báo (`notification-templates/render.ts`, chạy ở api + worker) và `@goppy/ui-kit` `formatMoney`
 * (admin + transparency).
 *
 * 🔴 NOTIF-MONEY-BIGINT (@UC9064): trước bản này `render.ts` có bản chép riêng đưa `Number(major)` vào Intl ⇒
 * `9007199254740993n` VND ra `9.007.199.254.740.992 ₫` (lệch 1 đồng; §3.1 "không float ở mọi tầng"), trong khi
 * bản của ui-kit đã đúng từ US-UI-0283. Nay cả hai gọi hàm này; ui-kit không thể là chỗ chứa vì nó phụ thuộc
 * `@goppy/domain`, không ngược lại.
 *
 * Cách làm: KHÔNG một bước nào qua `number`.
 *  - digits = 0 (VND/JPY): `Intl.NumberFormat.prototype.format` nhận BigInt trực tiếp.
 *  - digits > 0: phần nguyên (BigInt) qua `formatToParts`, phần lẻ ghép bằng chuỗi từ `abs % 10^digits`.
 *    Dấu suy từ `amountMinor` chứ không từ phần nguyên: `-0n === 0n` ⇒ `-50n` USD sẽ mất dấu nếu suy từ `major`
 *    (US-UI-0283) — khi phần nguyên bằng 0 ta MƯỢN `-1n` chỉ để Intl đặt dấu, rồi trả chữ số về `0`.
 *
 * Đầu ra cho mọi giá trị an toàn (≤ 2^53) trùng từng byte với bản cũ (NBSP của Intl giữ nguyên) — chỉ khác ở số lớn.
 */
import { minorUnitDigits } from "./currency-minor-units.js";

/**
 * @param amountMinor BigInt minor units (nguồn sự thật, không đổi).
 * @param currency    Mã ISO 4217 (số chữ số lẻ đọc từ `minorUnitDigits`).
 * @param intlLocale  BCP-47 cho Intl (`vi-VN`, `en-US`, …).
 */
export function formatMoneyMinorIntl(amountMinor: bigint, currency: string, intlLocale: string): string {
  const digits = minorUnitDigits(currency);
  const nf = new Intl.NumberFormat(intlLocale, {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (digits === 0) {
    return nf.format(amountMinor);
  }

  const divisor = 10n ** BigInt(digits);
  const negative = amountMinor < 0n;
  const abs = negative ? -amountMinor : amountMinor;
  const major = abs / divisor;
  const fractionStr = (abs % divisor).toString().padStart(digits, "0");
  const borrowedSign = negative && major === 0n;
  const parts = nf.formatToParts(borrowedSign ? -1n : negative ? -major : major);
  return parts
    .map((p) => {
      if (p.type === "fraction") return fractionStr;
      if (p.type === "integer" && borrowedSign) return "0";
      return p.value;
    })
    .join("");
}
